// Keeps deployed clients on the latest build. Never reloads on its own: it announces the update
// (window event 'vc:update-available') and the UpdateBanner lets the user choose when to apply.
const CURRENT_VERSION = process.env.REACT_APP_BUILD_VERSION || 'dev';

// After this long away from the app, returning forces the latest version (auto restart).
const AWAY_RELOAD_MS = 60 * 60 * 1000; // 1 hour

let pendingVersion = null;
let waitingWorker = null;
let hiddenAt = null;

const announce = (version) => {
  if (pendingVersion === version) return;
  pendingVersion = version;
  window.dispatchEvent(new CustomEvent('vc:update-available', { detail: { version } }));
};

export const onUpdateAvailable = (callback) => {
  const handler = (e) => callback(e.detail);
  window.addEventListener('vc:update-available', handler);
  if (pendingVersion) callback({ version: pendingVersion });
  return () => window.removeEventListener('vc:update-available', handler);
};

// Called by the banner: activate the waiting worker (if any) and reload once it takes control
export const applyUpdate = async () => {
  if (waitingWorker) {
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!reloaded) { reloaded = true; window.location.reload(); }
    }, { once: true });
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    setTimeout(() => { if (!reloaded) window.location.reload(); }, 1500);
    return;
  }
  window.location.reload();
};

export const checkForNewVersion = async () => {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const { version } = await res.json();
    if (version && version !== CURRENT_VERSION && CURRENT_VERSION !== 'dev') {
      const reg = await navigator.serviceWorker?.getRegistration();
      if (reg) await reg.update().catch(() => {});
      announce(version);
    }
  } catch {
    // offline or blocked: ignore
  }
};

// Returned to the app after being away >= AWAY_RELOAD_MS: silently jump to the latest
// version (auto restart) instead of showing the banner. Only reloads if a newer build exists.
const forceLatestVersion = async () => {
  if (CURRENT_VERSION === 'dev') return;
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const { version } = await res.json();
    if (!version || version === CURRENT_VERSION) return; // already latest, no restart needed
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) await reg.update().catch(() => {});
    await applyUpdate(); // activates waiting worker (if any) then reloads
  } catch {
    // offline or blocked: keep current version
  }
};

const handleReturn = () => {
  const awayMs = hiddenAt ? Date.now() - hiddenAt : 0;
  hiddenAt = null;
  if (awayMs >= AWAY_RELOAD_MS) {
    forceLatestVersion();
    return;
  }
  refresh();
};

const refresh = async () => {
  const reg = await navigator.serviceWorker?.getRegistration();
  if (reg) reg.update().catch(() => {});
  checkForNewVersion();
};

export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
      const hadController = !!navigator.serviceWorker.controller;

      const trackWaiting = (worker) => {
        if (!worker) return;
        waitingWorker = worker;
        if (hadController) announce(`sw-${Date.now()}`);
      };
      if (registration.waiting) trackWaiting(registration.waiting);

      registration.addEventListener('updatefound', () => {
        const worker = registration.installing;
        worker?.addEventListener('statechange', () => {
          if (worker.state === 'installed') trackWaiting(worker);
        });
      });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          hiddenAt = Date.now();
        } else {
          handleReturn();
        }
      });
      window.addEventListener('focus', handleReturn);
      setInterval(refresh, 5 * 60 * 1000);
      checkForNewVersion();
    } catch (error) {
      console.log('[PWA] Service Worker registration failed:', error);
    }
  });
};
